/**
 * FormReadyContext - "Wait for Data" Promise System
 * 
 * This context manages form rendering readiness state for PDF generation.
 * PDF capture should ONLY proceed when the form signals it's fully rendered.
 * 
 * Usage:
 * 1. Wrap your form with <FormReadyProvider>
 * 2. Use useFormReady() hook in FormFieldRenderer to signal readiness
 * 3. Use waitForFormReady() before PDF generation
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const FormReadyContext = createContext(null);

/**
 * FormReadyProvider - Wraps form components to track rendering state
 */
export const FormReadyProvider = ({ children, onReady }) => {
  const [isReady, setIsReady] = useState(false);
  const [pendingFields, setPendingFields] = useState(new Set());
  const [errors, setErrors] = useState([]);
  const readyCallbackRef = useRef(onReady);
  const timeoutRef = useRef(null);
  
  // Update callback ref when prop changes
  useEffect(() => {
    readyCallbackRef.current = onReady;
  }, [onReady]);
  
  // Register a field as pending (loading/rendering)
  const registerPendingField = useCallback((fieldId) => {
    setPendingFields(prev => new Set([...prev, fieldId]));
    setIsReady(false);
  }, []);
  
  // Mark a field as ready (finished rendering)
  const markFieldReady = useCallback((fieldId) => {
    setPendingFields(prev => {
      const next = new Set(prev);
      next.delete(fieldId);
      return next;
    });
  }, []);
  
  // Mark a field as having an error
  const markFieldError = useCallback((fieldId, error) => {
    setErrors(prev => [...prev, { fieldId, error, timestamp: new Date().toISOString() }]);
    // Still mark as ready so we don't block forever
    markFieldReady(fieldId);
  }, [markFieldReady]);
  
  // Check if all fields are ready
  useEffect(() => {
    if (pendingFields.size === 0 && !isReady) {
      // Small delay to ensure DOM is fully painted
      timeoutRef.current = setTimeout(() => {
        setIsReady(true);
        if (readyCallbackRef.current) {
          readyCallbackRef.current({ errors });
        }
      }, 100);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [pendingFields.size, isReady, errors]);
  
  // Reset the ready state (for re-rendering)
  const reset = useCallback(() => {
    setIsReady(false);
    setPendingFields(new Set());
    setErrors([]);
  }, []);
  
  // Promise-based wait for ready state
  const waitForReady = useCallback((timeoutMs = 10000) => {
    return new Promise((resolve, reject) => {
      if (isReady) {
        resolve({ success: true, errors });
        return;
      }
      
      const checkInterval = setInterval(() => {
        if (isReady) {
          clearInterval(checkInterval);
          clearTimeout(timeout);
          resolve({ success: true, errors });
        }
      }, 50);
      
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error(`Form not ready after ${timeoutMs}ms. Pending fields: ${[...pendingFields].join(', ')}`));
      }, timeoutMs);
    });
  }, [isReady, errors, pendingFields]);
  
  const value = {
    isReady,
    pendingFields: [...pendingFields],
    errors,
    registerPendingField,
    markFieldReady,
    markFieldError,
    reset,
    waitForReady
  };
  
  return (
    <FormReadyContext.Provider value={value}>
      {children}
    </FormReadyContext.Provider>
  );
};

/**
 * useFormReady - Hook to access form ready state
 */
export const useFormReady = () => {
  const context = useContext(FormReadyContext);
  if (!context) {
    // Return no-op functions if not in a FormReadyProvider
    // This allows FormFieldRenderer to work without the provider
    return {
      isReady: true,
      pendingFields: [],
      errors: [],
      registerPendingField: () => {},
      markFieldReady: () => {},
      markFieldError: () => {},
      reset: () => {},
      waitForReady: () => Promise.resolve({ success: true, errors: [] })
    };
  }
  return context;
};

/**
 * useFieldReady - Hook for individual fields to signal their ready state
 * 
 * Usage in field components:
 * const { signalReady, signalError } = useFieldReady(field.id);
 * useEffect(() => { signalReady(); }, [data]);
 */
export const useFieldReady = (fieldId) => {
  const { registerPendingField, markFieldReady, markFieldError } = useFormReady();
  const registeredRef = useRef(false);
  
  // Register on mount
  useEffect(() => {
    if (!registeredRef.current && fieldId) {
      registerPendingField(fieldId);
      registeredRef.current = true;
    }
  }, [fieldId, registerPendingField]);
  
  const signalReady = useCallback(() => {
    if (fieldId) {
      markFieldReady(fieldId);
    }
  }, [fieldId, markFieldReady]);
  
  const signalError = useCallback((error) => {
    if (fieldId) {
      markFieldError(fieldId, error);
    }
  }, [fieldId, markFieldError]);
  
  return { signalReady, signalError };
};

export default FormReadyContext;
